import BillingsOrderRailyTaxiDraftResponse, {
  BillingsOrderRailyTaxiDraftResponseData,
  BillingsOrderRailyTaxiDraftResponseDataBillingNode,
  BillingsOrderRailyTaxiDraftResponseDataBonusType,
  BillingsOrderRailyTaxiDraftResponseDataCargoOrder,
  BillingsOrderRailyTaxiDraftResponseDataContract,
  BillingsOrderRailyTaxiDraftResponseDataContractBillingModel,
  BillingsOrderRailyTaxiDraftResponseDataContractTaxiCompany,
  BillingsOrderRailyTaxiDraftResponseDataPenaltyType,
} from "../../../../../api/raily/billings/order/raily-taxi/draft/billings-order-raily-taxi-draft.response";
import BillingModel from "../../../../../types/billing-model";
import BillingsOrderRailyTaxiDraft, {
  BillingsOrderRailyTaxiDraftBillingNode,
  BillingsOrderRailyTaxiDraftBonusType,
  BillingsOrderRailyTaxiDraftCargoOrder,
  BillingsOrderRailyTaxiDraftContract,
  BillingsOrderRailyTaxiDraftContractTaxiCompany,
  BillingsOrderRailyTaxiDraftPenaltyType,
} from "./billings-order-raily-taxi-draft";

const createBillingNode = (
  node: BillingsOrderRailyTaxiDraftResponseDataBillingNode,
): BillingsOrderRailyTaxiDraftBillingNode => {
  return {
    allowCharge: node.allowCharge,
    description: node.description,
    distance: node.distance,
    editableCoordinates: node.editableCoordinates,
    haltingTime: node.haltingTime,
    highwayCharge: node.highwayCharge,
    lat: node.lat,
    lon: node.lon,
    nodeUuid: node.nodeId,
    plannedDistance: node.plannedDistance,
    uuid: node.id,
    checkoutDate: node.checkoutDate,
    checkoutEventUuid: node.checkoutEventId,
    orderUuid: node.orderId,
    plannedDate: node.plannedDate,
  };
};

const createBillingNodes = (
  nodes: BillingsOrderRailyTaxiDraftResponseDataBillingNode[],
): BillingsOrderRailyTaxiDraftBillingNode[] => {
  return nodes.map(createBillingNode);
};

const createCargoOrder = (
  cargoOrder: BillingsOrderRailyTaxiDraftResponseDataCargoOrder,
): BillingsOrderRailyTaxiDraftCargoOrder => {
  return {
    humanId: cargoOrder.humanId,
  };
};

const createModel = (
  model: BillingsOrderRailyTaxiDraftResponseDataContractBillingModel,
): BillingModel => {
  switch (model) {
    case BillingsOrderRailyTaxiDraftResponseDataContractBillingModel.AB:
      return BillingModel.AB;
    case BillingsOrderRailyTaxiDraftResponseDataContractBillingModel.ABA:
      return BillingModel.ABA;
    case BillingsOrderRailyTaxiDraftResponseDataContractBillingModel.OTHER:
      return BillingModel.OTHER;
    case BillingsOrderRailyTaxiDraftResponseDataContractBillingModel.SABS:
      return BillingModel.SABS;
  }
};

const createTaxiCorporation = (
  taxiCorporation: BillingsOrderRailyTaxiDraftResponseDataContractTaxiCompany,
): BillingsOrderRailyTaxiDraftContractTaxiCompany => {
  return {
    companyName: taxiCorporation.companyName,
  };
};

const createContract = (
  contract: BillingsOrderRailyTaxiDraftResponseDataContract,
): BillingsOrderRailyTaxiDraftContract => {
  return {
    distanceRate: contract.distanceRate,
    haltingTimeAfterMinutes: contract.haltingTimeAfterMinutes,
    haltingTimeRate: contract.haltingTimeRate,
    model: createModel(contract.model),
    taxiCorporation: createTaxiCorporation(contract.taxiCorporation),
  };
};

const createBonusType = (
  bonusType: BillingsOrderRailyTaxiDraftResponseDataBonusType | undefined,
): BillingsOrderRailyTaxiDraftBonusType | undefined => {
  switch (bonusType) {
    case BillingsOrderRailyTaxiDraftResponseDataBonusType.DISTANCE_RATE_CHANGED:
      return BillingsOrderRailyTaxiDraftBonusType.DISTANCE_RATE_CHANGED;
    case BillingsOrderRailyTaxiDraftResponseDataBonusType.HALTING_RATE_CHANGED:
      return BillingsOrderRailyTaxiDraftBonusType.HALTING_RATE_CHANGED;
    case BillingsOrderRailyTaxiDraftResponseDataBonusType.OTHER:
      return BillingsOrderRailyTaxiDraftBonusType.OTHER;
    case BillingsOrderRailyTaxiDraftResponseDataBonusType.SHORT_ROUTE:
      return BillingsOrderRailyTaxiDraftBonusType.SHORT_ROUTE;
  }
};

const createPenaltyType = (
  penaltyType: BillingsOrderRailyTaxiDraftResponseDataPenaltyType | undefined,
): BillingsOrderRailyTaxiDraftPenaltyType | undefined => {
  switch (penaltyType) {
    case BillingsOrderRailyTaxiDraftResponseDataPenaltyType.BEING_LATE:
      return BillingsOrderRailyTaxiDraftPenaltyType.BEING_LATE;
    case BillingsOrderRailyTaxiDraftResponseDataPenaltyType.GUIDELINE_IGNORED:
      return BillingsOrderRailyTaxiDraftPenaltyType.GUIDELINE_IGNORED;
    case BillingsOrderRailyTaxiDraftResponseDataPenaltyType.HIGHWAY_NOT_ALLOWED:
      return BillingsOrderRailyTaxiDraftPenaltyType.HIGHWAY_NOT_ALLOWED;
    case BillingsOrderRailyTaxiDraftResponseDataPenaltyType.INCOMPATIBLE_CAR:
      return BillingsOrderRailyTaxiDraftPenaltyType.INCOMPATIBLE_CAR;
    case BillingsOrderRailyTaxiDraftResponseDataPenaltyType.OPTIMIZATION_REFUSED:
      return BillingsOrderRailyTaxiDraftPenaltyType.OPTIMIZATION_REFUSED;
    case BillingsOrderRailyTaxiDraftResponseDataPenaltyType.OTHER:
      return BillingsOrderRailyTaxiDraftPenaltyType.OTHER;
    case BillingsOrderRailyTaxiDraftResponseDataPenaltyType.SUBOPTIMAL_RIDE:
      return BillingsOrderRailyTaxiDraftPenaltyType.SUBOPTIMAL_RIDE;
    default:
      return undefined;
  }
};

const createData = (
  data: BillingsOrderRailyTaxiDraftResponseData,
): BillingsOrderRailyTaxiDraft => {
  return {
    billingNodes: createBillingNodes(data.billingNodes),
    bonus: data.bonus,
    cargoOrder: createCargoOrder(data.cargoOrder),
    contract: createContract(data.contract),
    date: data.date,
    distance: data.distance,
    distanceAmount: data.distanceAmount,
    haltingAmount: data.haltingAmount,
    highwayCharge: data.highwayCharge,
    penalty: data.penalty,
    planEntryUuid: data.planEntryId,
    totalAmount: data.totalAmount,
    bonusType: createBonusType(data.bonusType),
    penaltyType: createPenaltyType(data.penaltyType),
  };
};

const create = (
  response: BillingsOrderRailyTaxiDraftResponse,
): BillingsOrderRailyTaxiDraft => {
  return createData(response.data);
};

const billingsOrderRailyTaxiDraftFactory = {
  create,
};

export default billingsOrderRailyTaxiDraftFactory;
